import {DecimalPipe} from '@angular/common';
import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {
  SystemUsageDailyView,
  SystemUsageMonthlyView,
  SystemUsageSummaryService,
  SystemUsageYearlyView
} from '../system-usage-summary.service';
import {PageTitleComponent} from '../../../../shared/commons/page-title/page-title.component';
import {GtNgDatepickerComponent} from '../../../../shared/commons/gt-ng-datepicker/gt-ng-datepicker.component';
import {ToastService} from '../../../../shared/commons/toast/toast-service';

const THAI_MONTHS = [
  'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
  'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
];

/** สรุปยอดรวมของตารางรายวันในช่วงที่เลือก ใช้กับกราฟสถานะการตรวจสอบ/อนุมัติ */
interface DailyTotals {
  reviewed: number;
  pendingReview: number;
  pendingApproval: number;
  approved: number;
  rejected: number;
}

@Component({
  selector: 'app-system-usage-summary-list',
  standalone: true,
  imports: [DecimalPipe, FormsModule, PageTitleComponent, GtNgDatepickerComponent],
  templateUrl: './system-usage-summary-list.component.html',
  styleUrl: './system-usage-summary-list.component.scss'
})
export class SystemUsageSummaryListComponent implements OnInit {
  private readonly service = inject(SystemUsageSummaryService);
  private readonly toastService = inject(ToastService);

  protected dateStart = signal<string>('');
  protected dateEnd = signal<string>('');
  /** ปี ค.ศ. ที่เลือกสำหรับรายงานรายเดือน */
  protected year = signal<number>(new Date().getFullYear());
  protected loading = signal<boolean>(false);

  protected dailyData = signal<SystemUsageDailyView[]>([]);
  protected monthlyData = signal<SystemUsageMonthlyView[]>([]);
  protected yearlyData = signal<SystemUsageYearlyView[]>([]);

  /** ปีที่ให้เลือกในรายงานรายเดือน: ปีที่มีข้อมูล + ปีปัจจุบัน (ค.ศ., เรียงล่าสุดก่อน) */
  protected yearOptions = computed<number[]>(() => {
    const years = new Set<number>(this.yearlyData().map(y => y.year));
    years.add(new Date().getFullYear());
    years.add(this.year());
    return [...years].sort((a, b) => b - a);
  });

  protected dailyTotals = computed<DailyTotals>(() => this.dailyData().reduce<DailyTotals>((acc, d) => ({
    reviewed: acc.reviewed + d.reviewed,
    pendingReview: acc.pendingReview + d.pendingReview,
    pendingApproval: acc.pendingApproval + d.pendingApproval,
    approved: acc.approved + d.approved,
    rejected: acc.rejected + d.rejected
  }), {reviewed: 0, pendingReview: 0, pendingApproval: 0, approved: 0, rejected: 0}));

  protected inspectionTotal = computed(() => this.dailyTotals().reviewed + this.dailyTotals().pendingReview);
  protected approvalTotal = computed(() => {
    const t = this.dailyTotals();
    return t.approved + t.pendingApproval + t.rejected;
  });

  /** % ผ่านตรวจสอบของทั้งปีที่เลือก (รวมทุกเดือน) */
  protected monthlyPassPct = computed(() => {
    const total = this.monthlyData().reduce((s, m) => s + m.totalRequests, 0);
    const pass = this.monthlyData().reduce((s, m) => s + m.passInspection, 0);
    return total > 0 ? Math.round(pass * 1000 / total) / 10 : 0;
  });

  /** อัตราการอนุมัติรวมทุกปี */
  protected yearlyApprovalRate = computed(() => {
    const total = this.yearlyData().reduce((s, y) => s + y.totalRequests, 0);
    const approved = this.yearlyData().reduce((s, y) => s + y.totalApproved, 0);
    return total > 0 ? Math.round(approved * 1000 / total) / 10 : 0;
  });

  ngOnInit(): void {
    this.search();
  }

  search(): void {
    this.loading.set(true);
    this.service.summarize({
      dateStart: this.dateStart() || undefined,
      dateEnd: this.dateEnd() || undefined,
      year: this.year()
    }).subscribe({
      next: res => {
        this.dateStart.set(res.dateStart);
        this.dateEnd.set(res.dateEnd);
        this.year.set(res.year);
        this.dailyData.set(res.daily);
        this.monthlyData.set(res.monthly);
        this.yearlyData.set(res.yearly);
        this.loading.set(false);
      },
      error: err => {
        this.loading.set(false);
        this.toastService.error(err?.error?.message ?? 'ไม่สามารถโหลดรายงานได้');
      }
    });
  }

  reset(): void {
    this.dateStart.set('');
    this.dateEnd.set('');
    this.year.set(new Date().getFullYear());
    this.search();
  }

  onYearChange(value: string | number): void {
    this.year.set(Number(value));
    this.search();
  }

  // ---------- การแสดงผล ----------

  /** dd/MM/พ.ศ. */
  protected thaiDate(iso: string): string {
    const [y, m, d] = iso.split('-').map(Number);
    return `${d}/${m}/${y + 543}`;
  }

  /** ชื่อเดือนภาษาไทย + ปี พ.ศ. */
  protected thaiMonth(iso: string): string {
    const [y, m] = iso.split('-').map(Number);
    return `${THAI_MONTHS[m - 1]} ${y + 543}`;
  }

  protected thaiYear(year: number): string {
    return String(year + 543);
  }

  protected duration(days: number | null): string {
    return days == null ? '-' : `${days.toLocaleString('en-US', {maximumFractionDigits: 1})} วัน`;
  }

  protected pct(part: number, total: number): number {
    return total > 0 ? (part / total) * 100 : 0;
  }

  // ---------- กราฟวงกลม (conic-gradient) ----------

  protected dailyInspectionBg(): string {
    const t = this.dailyTotals();
    const total = t.reviewed + t.pendingReview;
    if (total <= 0) {
      return 'conic-gradient(#e5e7eb 0deg 360deg)';
    }
    const reviewedDeg = (t.reviewed / total) * 360;
    return `conic-gradient(#22c55e 0deg ${reviewedDeg}deg, #f59e0b ${reviewedDeg}deg 360deg)`;
  }

  protected dailyApprovalBg(): string {
    const t = this.dailyTotals();
    const total = t.approved + t.pendingApproval + t.rejected;
    if (total <= 0) {
      return 'conic-gradient(#e5e7eb 0deg 360deg)';
    }
    const approvedDeg = (t.approved / total) * 360;
    const pendingEnd = approvedDeg + (t.pendingApproval / total) * 360;
    return `conic-gradient(#22c55e 0deg ${approvedDeg}deg, #f59e0b ${approvedDeg}deg ${pendingEnd}deg, #ef4444 ${pendingEnd}deg 360deg)`;
  }

  protected monthlyPassBg(): string {
    const passDeg = (this.monthlyPassPct() / 100) * 360;
    return `conic-gradient(#3b82f6 0deg ${passDeg}deg, #e5e7eb ${passDeg}deg 360deg)`;
  }

  protected yearlyApprovalBg(): string {
    const approveDeg = (this.yearlyApprovalRate() / 100) * 360;
    return `conic-gradient(#8b5cf6 0deg ${approveDeg}deg, #e5e7eb ${approveDeg}deg 360deg)`;
  }
}
